import type { Vehicle } from '@zuruflex/types';
import { Fuel, Gauge, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn.js';
import { Badge } from '../ui/badge.js';
import { Card } from '../ui/card.js';
import { Money, type MoneyConversion } from './money.js';
import { VehicleStatusChip } from './status-chip.js';

export interface VehicleCardProps {
  vehicle: Vehicle;
  onSelect?: (vehicle: Vehicle) => void;
  showStatus?: boolean;
  /** Overrides the default translated "Book" label. */
  actionLabel?: string;
  /**
   * Which rate to show: what a customer would pay, or the partner's own.
   *
   * A customer's grid shows the all-in figure, because a car that cannot be
   * hired without a driver costs what it costs. A staff grid shows the base
   * rate — an operator scanning a fleet is looking at the vehicle, not at a
   * driver's wage — so the two callers have to say which they mean rather
   * than share a default that is wrong for one of them.
   */
  priceMode?: 'base' | 'allIn';
  /**
   * Indicative conversion of the daily rate, shown beneath it. The card
   * does not fetch rates itself — the page supplies them.
   */
  convertDailyRate?: (amount: number, currency: string) => MoneyConversion | null;
  className?: string;
}

/**
 * The fleet tile, shared by the landing page and the admin fleet grid.
 *
 * Composites under `components/flex` may translate themselves; the
 * primitives under `components/ui` never do, so they stay reusable outside
 * an i18n provider.
 */
export function VehicleCard({
  vehicle,
  onSelect,
  showStatus = false,
  actionLabel,
  priceMode = 'base',
  convertDailyRate,
  className,
}: VehicleCardProps) {
  const { t } = useTranslation('common');
  const cover = vehicle.images[0];
  const rate = priceMode === 'allIn' ? vehicle.effectiveDailyRate : vehicle.dailyRate;

  return (
    <Card
      variant="default"
      interactive={Boolean(onSelect)}
      onClick={onSelect ? () => onSelect(vehicle) : undefined}
      className={cn('group flex flex-col overflow-hidden', className)}
    >
      <div className="relative aspect-[16/10] shrink-0 overflow-hidden bg-surface-sunken">
        {cover ? (
          <img
            src={cover.url}
            alt={cover.alt ?? `${vehicle.make} ${vehicle.model}`}
            className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            loading="lazy"
          />
        ) : (
          // No photo yet: a soft grid keeps the tile from looking broken.
          <div className="zuruflex-grid-bg-light flex size-full items-center justify-center">
            <span className="text-xs font-bold uppercase tracking-widest text-text-subtle">
              {t(`vehicleClass.${vehicle.vehicleClass}`)}
            </span>
          </div>
        )}

        {/* Whether a driver comes with the car is the kind of thing someone
            scans a grid for, so it sits on the photo rather than below the
            fold with the features.

            On the same translucent, blurred backing as the status chip
            opposite. Without it an outline badge is grey text inside a
            near-white border directly on a photograph — legible over a dark
            car, invisible over a pale one, which is most of this fleet. */}
        {vehicle.driverPolicy === 'SELF_DRIVE_ONLY' ? null : (
          <div className="absolute right-3 top-3 rounded-pill bg-surface-raised/90 p-0.5 shadow-soft backdrop-blur-sm">
            <Badge variant={vehicle.driverPolicy === 'DRIVER_ONLY' ? 'brand' : 'outline'}>
              {t(`driverPolicy.${vehicle.driverPolicy}`)}
            </Badge>
          </div>
        )}

        {showStatus ? (
          <div className="absolute left-3 top-3 rounded-pill bg-surface-raised/90 p-0.5 shadow-soft backdrop-blur-sm">
            <VehicleStatusChip
              status={vehicle.status}
              label={t(`vehicleStatus.${vehicle.status}`)}
            />
          </div>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col gap-3 p-5">
        <div>
          <h3 className="font-display text-lg font-bold leading-tight">
            {vehicle.make} {vehicle.model}
          </h3>
          <p className="mt-0.5 text-sm text-text-muted">{vehicle.year}</p>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-text-muted">
          <span className="inline-flex items-center gap-1.5">
            <Users className="size-3.5" />
            {t('unit.seat', { count: vehicle.seats })}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Gauge className="size-3.5" />
            {t(`transmission.${vehicle.transmission}`)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Fuel className="size-3.5" />
            {t(`fuelType.${vehicle.fuelType}`)}
          </span>
        </div>

        {vehicle.features.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {vehicle.features.slice(0, 3).map((feature) => (
              <Badge key={feature}>{feature}</Badge>
            ))}
            {vehicle.features.length > 3 ? (
              <Badge variant="outline">+{vehicle.features.length - 3}</Badge>
            ) : null}
          </div>
        ) : null}

        <div className="mt-auto flex items-end justify-between gap-3 border-t border-border-default pt-3">
          <Money
            amount={rate}
            currency={vehicle.currency}
            converted={convertDailyRate?.(rate, vehicle.currency)}
            amountClassName="font-display text-xl font-bold"
            suffix={<span className="text-xs text-text-muted">{t('unit.perDay')}</span>}
          />
          {onSelect ? (
            <span className="rounded-pill bg-accent px-3.5 py-1.5 text-xs font-semibold text-accent-text transition-colors group-hover:bg-yellow-400">
              {actionLabel ?? t('action.book')}
            </span>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
